"use client"
import { useState, useEffect } from "react"

/**
 * Data Saver preference.
 *
 * Most Zimbabwean users are on metered, WhatsApp-first data bundles where a
 * heavy page literally costs money. When Data Saver is on we load lighter
 * images, skip auto-advancing carousels, and defer heavy embeds (maps).
 *
 * Resolution order: an explicit user choice (localStorage) always wins; if the
 * user has never chosen, we auto-detect a slow/metered connection and default
 * ON for it. Mirrors the CookieConsent pattern (localStorage + a window event
 * so every mounted component updates the instant the toggle flips).
 */
const KEY = "ze_data_saver"
const EVENT = "ze:datasaver"

/** True when the browser reports a slow or explicitly data-saving connection. */
function autoDetect(): boolean {
  if (typeof navigator === "undefined") return false
  const c: any =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection
  if (!c) return false
  if (c.saveData) return true
  const t = c.effectiveType
  return t === "slow-2g" || t === "2g" || t === "3g"
}

/** Current setting: explicit user choice wins, otherwise auto-detect. */
export function readDataSaver(): boolean {
  if (typeof window === "undefined") return false
  const stored = localStorage.getItem(KEY)
  if (stored === "true") return true
  if (stored === "false") return false
  return autoDetect()
}

export function useDataSaver(): { dataSaver: boolean; setDataSaver: (v: boolean) => void } {
  // Start false on server + first client render (matches SSR) then correct in
  // the effect — avoids a hydration mismatch.
  const [dataSaver, setState] = useState(false)

  useEffect(() => {
    setState(readDataSaver())
    const onChange = () => setState(readDataSaver())
    window.addEventListener(EVENT, onChange)
    return () => window.removeEventListener(EVENT, onChange)
  }, [])

  function setDataSaver(v: boolean) {
    localStorage.setItem(KEY, v ? "true" : "false")
    window.dispatchEvent(new Event(EVENT))
    setState(v)
  }

  return { dataSaver, setDataSaver }
}
